// src/rms/config/supabaseClient.js
// ============================================================================
// Supabase client — created lazily
// ============================================================================
// Only used when VITE_RMS_BACKEND=supabase. The default local adapter never
// touches it.
//
// The client is built on FIRST USE rather than at module load. supabase-js
// validates its URL in the constructor and throws on an empty string, so
// constructing eagerly meant an unconfigured local install crashed on boot with
// "supabaseUrl is required" — before rendering a single pixel, for a dependency
// it was never going to call.
// ============================================================================

import { createClient } from "@supabase/supabase-js";

const url = import.meta.env.VITE_SUPABASE_URL;
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

export const isSupabaseConfigured = !!(url && anonKey);

if (!isSupabaseConfigured && import.meta.env.VITE_RMS_BACKEND === "supabase") {
  console.error("VITE_RMS_BACKEND=supabase but VITE_SUPABASE_URL / _ANON_KEY are not set.");
}

let client = null;

/** The Supabase client. Throws only if actually used while unconfigured. */
export function getSupabase() {
  if (!isSupabaseConfigured) {
    throw new Error(
      "Supabase is not configured. Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY, " +
        "or run with VITE_RMS_BACKEND=local (the default)."
    );
  }
  if (!client) {
    client = createClient(url, anonKey, {
      auth: {
        autoRefreshToken: true,
        persistSession: true,
        // Was false, which silently broke every email-based sign-in: a magic
        // link, a password recovery and an email confirmation all land on the
        // site with the token in the URL, and a client told not to look for it
        // discards it. The visible symptom is landing on the marketing page
        // still signed out -- no error, nothing in the console, nothing in the
        // auth logs, because the exchange was never attempted.
        //
        // Safe against the Referer leak that motivated turning it off: Supabase
        // puts the token in the URL FRAGMENT, and fragments are not sent in
        // Referer headers, so the ad units on this site never see it. That is a
        // different case from /join, whose token arrives in the query string
        // and is still stripped with replaceState on mount.
        detectSessionInUrl: true,
      },
    });
  }
  return client;
}

/**
 * Proxy so existing `supabase.from(...)` call sites keep working unchanged,
 * while construction is still deferred to first property access.
 */
export const supabase = new Proxy(
  {},
  {
    get(_target, prop) {
      const instance = getSupabase();
      const value = instance[prop];
      return typeof value === "function" ? value.bind(instance) : value;
    },
  }
);

export default supabase;
